import { Autocomplete } from '@mantine/core';
import React, { useEffect, useState } from 'react';

import { loadProductTypeOptions } from './productTypeOptions';

interface ProductTypeSelectProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    size?: 'xs' | 'sm' | 'md';
    disabled?: boolean;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    autoFocus?: boolean;
}

/**
 * A product's type: one of the spellings this brand already uses, or a new one typed in.
 *
 * Free text with suggestions rather than a fixed list on purpose. `product_type` is a
 * per-brand string with hidden readers: SERVICE hides a product from inventory, and the
 * combo spellings drive combo logic. A fixed list would either normalise a brand's existing
 * values or refuse them; suggestions preserve them and still steer new entries to match.
 */
const ProductTypeSelect: React.FC<ProductTypeSelectProps> = ({
    value,
    onChange,
    label = 'Type',
    placeholder = 'Optional',
    size,
    disabled,
    onBlur,
    onKeyDown,
    autoFocus,
}) => {
    const [options, setOptions] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        void loadProductTypeOptions().then((types) => {
            if (!cancelled) setOptions(types);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Autocomplete
            label={label}
            placeholder={placeholder}
            data={options}
            value={value}
            onChange={onChange}
            size={size}
            disabled={disabled}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
        />
    );
};

export default ProductTypeSelect;
